import { User } from "@shared/types/user";
import { onAuthStateChanged } from "firebase/auth";
import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { auth } from "../../../config/firebase";
import { usePresence } from "../../presence/hooks/usePresence";
import { useAuth as useAuthHook } from "../hooks/useAuth";
import { authService } from "../services/authService";
import { userService } from "../services/userService";
import { useAuthStore } from "../store/authStore";

/**
 * Interface for the authentication context
 */
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication provider component
 * Manages authentication state and provides authentication methods
 * Uses the useAuth hook for authentication logic
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    login,
    register,
    logout,
    loading: isLoading,
    error,
    clearError,
  } = useAuthHook();

  const { user, isAuthenticated } = useAuthStore();
  const [isInitializing, setIsInitializing] = useState(true);

  usePresence();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && firebaseUser.emailVerified) {
        try {
          const token = await firebaseUser.getIdToken();
          userService.setAuthToken(token);
          authService.startTokenRefresh();
        } catch (err) {
          console.error('Error syncing auth token:', err);
          useAuthStore.getState().clearAuth();
        }
      } else if (!firebaseUser) {
        const { isAuthenticated: storeAuth, clearAuth } = useAuthStore.getState();
        if (storeAuth) clearAuth();
      }
      setIsInitializing(false);
    });

    return () => unsubscribe();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isInitializing,
    login,
    register,
    logout,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the auth context
 * @returns Auth context value
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
